import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  createElement,
  createRegion,
  createTable,
  createTablesRegion,
  deleteElement,
  deleteRegion,
  deleteTable,
  getRestaurantSchema,
  updateElement,
  updateRegion,
  updateTable,
  type ElementCreatePayload,
  type ElementUpdatePayload,
  type RegionPayload,
  type SchemaElement,
  type SchemaRegion,
  type TableCreatePayload,
  type TableUpdatePayload,
} from "../api/restaurantSchema";

export const SCHEMA_KEY = ["restaurantSchema"] as const;

// „Живата" схема (Резервации): маси се трият/добавят от друг таб или
// устройство, а WS за схемата няма — пита се на интервал и при фокус.
const LIVE_SCHEMA_POLL_MS = 60_000;

/**
 * Схемата на заведението. Редакторите (схема, конфигурация на маси) я
 * държат 5 минути — те сами обновяват кеша след всеки запис. С `live`
 * (Резервации) е винаги пресна: поле на минута, докато табът е видим, и при
 * връщане към таба/прозореца — иначе изтрита маса стои на платното и 422
 * „масата не съществува" се повтаря до презареждане.
 */
export const useRestaurantSchema = ({ live = false }: { live?: boolean } = {}) =>
  useQuery({
    queryKey: SCHEMA_KEY,
    queryFn: getRestaurantSchema,
    ...(live
      ? {
          staleTime: 0,
          refetchInterval: LIVE_SCHEMA_POLL_MS,
          refetchIntervalInBackground: false,
          refetchOnWindowFocus: "always" as const,
        }
      : { staleTime: 1000 * 60 * 5 }),
  });

// Сменя един елемент в кеша на място — местенето трябва да е мигновено,
// без да чака отговора (иначе масата „подскача" назад до refetch-а).
const patchCachedElement = (
  queryClient: QueryClient,
  elementId: number,
  patch: Partial<SchemaElement>,
) => {
  queryClient.setQueryData<SchemaRegion[]>(SCHEMA_KEY, (regions) => {
    if (!regions) return regions;
    const moved = regions
      .flatMap((region) => region.elements)
      .find((element) => element.id === elementId);
    if (!moved) return regions;
    const updated = { ...moved, ...patch };
    return regions.map((region) => ({
      ...region,
      elements:
        region.id === updated.regionId
          ? [
              ...region.elements.filter((element) => element.id !== elementId),
              updated,
            ].sort((left, right) => left.id - right.id)
          : region.elements.filter((element) => element.id !== elementId),
    }));
  });
};

const replaceCachedRegion = (queryClient: QueryClient, region: SchemaRegion) => {
  queryClient.setQueryData<SchemaRegion[]>(SCHEMA_KEY, (regions) =>
    regions?.map((cached) => (cached.id === region.id ? region : cached)),
  );
};

export const useCreateRegion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RegionPayload) => createRegion(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true }),
  });
};

export const useUpdateRegion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: RegionPayload }) =>
      updateRegion(id, data),
    onSuccess: (region) => replaceCachedRegion(queryClient, region),
  });
};

export const useDeleteRegion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteRegion(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true }),
  });
};

// Новият елемент (маса) влиза в кеша веднага (преди refetch-а) — следващият предложен
// номер вече я вижда и бързото добавяне с Enter не удря 409.
const appendCachedElement = (queryClient: QueryClient, element: SchemaElement) => {
  queryClient.setQueryData<SchemaRegion[]>(SCHEMA_KEY, (regions) =>
    regions?.map((region) =>
      region.id === element.regionId &&
      !region.elements.some((cached) => cached.id === element.id)
        ? { ...region, elements: [...region.elements, element] }
        : region,
    ),
  );
};

export const useCreateElement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ElementCreatePayload) => createElement(data),
    onSuccess: (element) => {
      appendCachedElement(queryClient, element);
      return queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true });
    },
  });
};

// Последната заявка за промяна на всеки елемент — по-стар отговор, дошъл след
// по-нов (две бързи промени на едно място), не бива да върне старата стойност.
const latestElementUpdate = new Map<number, number>();
let elementUpdateSequence = 0;

/**
 * Оптимистична промяна на елемент: кешът се сменя веднага. НЕ отменя текущо
 * презареждане на схемата (`cancelQueries` би го върнало назад и би изгубило
 * току-що създаден елемент — „Дублирай", после веднага местене); вместо това,
 * ако такова презареждане е в полет, след последната промяна се пуска още
 * едно. Редакторът (`/elements`) и „Конфигурация на маси" (`/tables`) делят
 * ЕДНА поредност на заявките — по-стар отговор не бива да върне стара
 * стойност, от който и път да е дошъл. `alwaysRefetch` — след успех схемата
 * се презарежда винаги (договорът на `/tables`: всяка мутация инвалидира).
 */
const useOptimisticElementUpdate = <Payload extends Partial<SchemaElement>>(
  send: (id: number, data: Payload) => Promise<SchemaElement>,
  { alwaysRefetch = false }: { alwaysRefetch?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Payload }) => send(id, data),
    onMutate: ({ id, data }) => {
      const sequence = ++elementUpdateSequence;
      latestElementUpdate.set(id, sequence);
      const previous = queryClient.getQueryData<SchemaRegion[]>(SCHEMA_KEY);
      patchCachedElement(queryClient, id, data);
      return { previous, sequence };
    },
    onError: (_error, { id }, context) => {
      if (context && latestElementUpdate.get(id) === context.sequence && context.previous) {
        queryClient.setQueryData(SCHEMA_KEY, context.previous);
        // `/tables`: грешката може да е от чужда промяна (изтрита маса, зает
        // номер) — връщането назад не стига, трябва пресен списък.
        if (alwaysRefetch) void queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true });
      } else {
        void queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true });
      }
    },
    onSuccess: (element, { id }, context) => {
      if (latestElementUpdate.get(id) !== context.sequence) return;
      patchCachedElement(queryClient, element.id, element);
      if (alwaysRefetch || queryClient.isFetching({ queryKey: SCHEMA_KEY, exact: true }) > 0) {
        void queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true });
      }
    },
  });
};

export const useUpdateElement = () =>
  useOptimisticElementUpdate<ElementUpdatePayload>(updateElement);

export const useDeleteElement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteElement(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true }),
  });
};

// ── Конфигурация на маси (`/restaurant-schema/tables*`) ────────────────────

export const useCreateTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TableCreatePayload) => createTable(data),
    onSuccess: (element) => {
      appendCachedElement(queryClient, element);
      void queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true });
    },
    // 409 от чужда маса със същия номер → пресен списък, за да се предложи друг.
    onError: () => queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true }),
  });
};

export const useUpdateTable = () =>
  useOptimisticElementUpdate<TableUpdatePayload>(updateTable, { alwaysRefetch: true });

export const useDeleteTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteTable(id),
    // И при грешка (404 — вече изтрита от друг): списъкът се опреснява.
    onSettled: () => queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true }),
  });
};

export const useCreateTablesRegion = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: RegionPayload) => createTablesRegion(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true }),
  });
};
